import {
  Box,
  Button,
  IconButton,
  Typography,
  styled,
  FormControlLabel,
  Switch,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { CustomInput, CustomSelect } from "../../../components-new/common";
import { GenDataOptions, scopeOptionList, TestDataOptions } from "../constants";
import { useDispatch, useSelector } from "react-redux";
import { setEditStep } from "../../../Redux/TestAuthoring/TestAuthoring";
import {
  useAllApiQuery,
  useDownloadedFileQuery,
  useRecordedTestDataListQuery,
  useUpdateTestDataQuery,
  useFileDataNameQuery,
  useGetAvailableDevices,
  useResetMultiExecution,
  useSystemidQuery,
} from "../../../useQuery";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNotification } from "../../../hooks";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { HelpIconButton } from "../../../components-new/help/help-modal";
import TestCaseSearch from "../../../components-new/common/TestCaseSearch";
import ModuleSelector from "../../../components-new/common/ModuleSelector";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import CustomDialog from "../../../components-new/common/CustomDialog";

const TestDataSetup = ({ item }) => {
  const dispatch = useDispatch();
  const notify = useNotification();
  const [selectedTestCase, setSelectedTestCase] = useState(item.dependentTC || item.Test_Case_Id);
  const [fileNameType, setFileNameType] = useState("");
  const [fileTobeUploaded, setfileToBeUploaded] = useState(null);
  const [platform, setPlatform] = useState("android");
  const [selectedDevice, setSelectedDevice] = useState("");

  const [cycles, setCycles] = useState([]);
  const [showDependencyModal, setShowDependencyModal] = useState(false);

  const selectedProductID = useSelector((state) => state.authoringSlice.selectedProduct);
  const selectedModuleID = useSelector((state) => state.authoringSlice.selectedComponent);
  const isMobile = useSelector((state) => state.authoringSlice.isMobile);
  const allTestSteps = useSelector((state) => state.executionReport.allTestSteps);

  const moduleKey = item.apimodule === 0 ? item.DependentModule : item.apimodule;

  const { data: downloadedFiles } = useDownloadedFileQuery(item.Test_Case_Id);
  const {
    data: refFiles,
    refetch: refetchFiles,
    isSuccess: isRefFilesSuccess,
  } = useFileDataNameQuery(selectedProductID);
  const { data: recordedTestDataList, refetch: refetchRecordedTestDataList } =
    useRecordedTestDataListQuery(item.idtest_step, selectedTestCase);

  const queryClient = useQueryClient();
  const [rangeStart = "", rangeEnd = ""] = item.gendatarange?.split("-") || [];

  const {
    data,
    mutate: updateTestData,
    isSuccess: isUpdateSuccess,
    isError,
    error,
    reset,
  } = useUpdateTestDataQuery({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["testCaseResults"] });
      queryClient.invalidateQueries({ queryKey: ["latestRecordings"] });
    },

    onError: (error) => {
      notify(
        error?.response?.data?.error || error?.response?.data?.message || "Update failed",
        "error",
      );

      // 👉 Same pattern as useSaveStepQuery
      const backendCycles = error?.response?.data?.cycles;

      if (backendCycles && Array.isArray(backendCycles) && backendCycles.length > 0) {
        setCycles(backendCycles); // store cycles from backend
        setShowDependencyModal(true); // open your CustomDialog
      }
    },
  });

  const { data: allApi, refetch: refetchAllApi } = useAllApiQuery(moduleKey || selectedModuleID, {
    enabled: item.apimodule !== 0 || !item.DependentModule,
  });

  const {
    data: availableDevices,
    isLoading: loadingDevices,
    refetch: refetchDevices,
  } = useGetAvailableDevices(platform);

  const { data: systemsid } = useSystemidQuery();

  useEffect(() => {
    if (item.testdata_source === "FromDB" && isError) {
      notify("Please enter valid query", "error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  useEffect(() => {
    if (item.testdata_source === "FromReferenceFile") {
      if (item.filename) {
        const simulatedEvent = {
          target: {
            value: item.filename,
          },
        };
        handleRefFileChange(simulatedEvent);
      }
      if (!item.scope) {
        handleChange("Single Run", "scope");
      }
    }
    if (item.testdata_source === "FromFile") {
      if (item.filename) {
        const simulatedEvent = {
          target: {
            value: item.filename,
          },
        };
        handleRefFileChange(simulatedEvent);
      }
      if (!item.scope) {
        handleChange("Single Run", "scope");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefFilesSuccess, item.filename, item.scope, item.testdata_source]);

  useEffect(() => {
    if (!isUpdateSuccess && !isError) return;
    const updatedData = data?.data?.data;

    if (updatedData && Object.values(updatedData)?.length) {
      const findStepByNumber = Object.values(updatedData).find(
        (step) => step.Step_Number === item.Step_Number,
      );

      if (findStepByNumber) {
        dispatch(setEditStep({ ...item, ...findStepByNumber }));
      }
    }

    if (isUpdateSuccess) {
      notify("The Test Data Source has been updated successfully.", "success");
    }

    if (isError) {
      notify(error?.response?.data?.message || "Update failed due to validation error", "error");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateSuccess, isError]);

  useEffect(() => {
    return () => {
      reset(); // 🔥 clears isSuccess / isError
    };
  }, []);

  useEffect(() => {
    if (selectedTestCase !== "" && selectedTestCase !== 0) {
      refetchRecordedTestDataList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTestCase]);

  useEffect(() => {
    if (item.testdata_source === "FromReferenceFile") {
      refetchFiles();
    }
  }, [item.testdata_source, refetchFiles]);

  useEffect(() => {
    if (item.testdata_source === "UseVariableFromTheScenario") refetchRecordedTestDataList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.testdata_source]);

  const handleChange = (value, field) => {
    dispatch(setEditStep({ ...item, [field]: value }));
  };

  const handleChangeMultiple = (value) => {
    dispatch(setEditStep({ ...item, ...value }));
  };

  const handleDateChange = (value, field, testdata) => {
    const parts = testdata.split("~");
    let index;
    if (field === "date") index = 2;
    if (field === "dateformat") index = 3;

    parts[index] = value;
    const updatedTestData = parts.join("~");

    dispatch(setEditStep({ ...item, TestData: updatedTestData }));
  };

  const handleGenChange = (value, field, testdata) => {
    if (!testdata) {
      testdata = "~~~";
    }

    const parts = testdata.split("~");

    if (field === "generateDateFormat") {
      parts[2] = value;
      const updatedTestData = parts.join("~");
      handleChangeMultiple({
        TestData: updatedTestData,
        gendateformat: value,
      });
    }
    if (field === "generateDateOffset") {
      parts[3] = value;
      const updatedTestData = parts.join("~");
      handleChangeMultiple({
        TestData: updatedTestData,
        gendateoffset: value,
      });
    }
  };

  const handleDateFormat = (value, field) => {
    const updatedValue = `${item.TestData}~${value}`;
    dispatch(setEditStep({ ...item, [field]: updatedValue }));
  };

  const handleAssertionValueChange = (value, field) => {
    const updatedValue = `${item.TestData}~${value}`;
    dispatch(setEditStep({ ...item, [field]: updatedValue }));
  };

  const handleSubmit = () => {
    const isExcel = fileNameType === "xlsx" || item.filename?.toLowerCase().endsWith(".xlsx");

    if (isExcel) {
      const isSheetEmpty = !item.sheet || item.sheet.trim() === "";
      const isCellEmpty = !item.cell || item.cell.trim() === "";

      if (isSheetEmpty && isCellEmpty) {
        notify("Sheet and Cell cannot be empty for Excel files.", "error");
        return;
      }

      if (isSheetEmpty) {
        notify("Sheet cannot be empty for Excel files.", "error");
        return;
      }

      if (isCellEmpty) {
        notify("Cell cannot be empty for Excel files.", "error");
        return;
      }
    }

    if (item.testdata_source === "FromApi") {
      if (
        !item.apiproduct ||
        !item.apimodule ||
        !item.apiid ||
        Number(item.apiid) === 0 ||
        item.apiid === ""
      ) {
        notify("Please select an API Product, Module and API before submitting.", "error");
        return;
      }
    }

    updateTestData({
      api_input: item,
      fromStrategy: false,
      fileData: fileTobeUploaded,
      // isNewFileUpload,
      modid: moduleKey || selectedModuleID,
    });
  };

  const handleRefFileChange = (event) => {
    const filename = event.target.value;
    if (filename) {
      const parts = filename.split(".");
      const fileExtension = parts.pop();
      handleChangeMultiple({ filename: filename, fromFileType: fileExtension });
      setFileNameType(fileExtension);
    }
  };

  const handleFileChange = (event) => {
    if (event.target.files.length > 0) {
      const filename = event.target.files[0].name;
      const parts = filename.split(".");
      const fileExtension = parts.pop();
      handleChangeMultiple({
        filename: event.target.files[0].name,
        fromFileType: fileExtension,
        sheet: "",
        cell: "",
        scope: "Single Run",
        endRange: "",
        filter: "",
      });
      setFileNameType(fileExtension);
      setfileToBeUploaded(event.target.files[0]);
    }
    event.target.value = "";
  };

  const { mutate: resetMultiExecution, isPending: isResetting } = useResetMultiExecution(
    item.idtest_step,
    {
      onSuccess: (data) => {
        notify(data?.message || "Reset execution successfully", "success");
      },
      onError: (error) => {
        notify(error?.message || "Failed to reset execution", "error");
      },
    },
  );

  const handleReset = () => {
    resetMultiExecution();
  };

  const StyledLabel = styled(Typography)`
    color: #8089a8;
    font-size: 13px;
    font-weight: 500;
  `;

  const renderDependencyActions = () => (
    <>
      <Button variant="contained" color="warning" onClick={() => setShowDependencyModal(false)}>
        Close
      </Button>
    </>
  );

  const hasFile = !!item.filename || !!fileTobeUploaded;

  const renderDependencyContent = () => {
    if (!cycles || cycles.length === 0) return null;

    return (
      <>
        <Typography sx={{ mb: 2 }}>
          This update creates one or more circular dependency loops. Please review the affected test
          cases below:
        </Typography>

        <List>
          {cycles.map((cycle, idx) => (
            <ListItem
              key={idx}
              sx={{
                mb: 1,
                border: "1px solid",
                borderColor: "warning.light",
                borderRadius: 1,
                backgroundColor: "#fff3f3",
              }}
            >
              <ListItemText
                primary={`Cycle ${idx + 1}`}
                secondary={
                  <>
                    {cycle.map((tc, i) => (
                      <div key={i}>
                        <strong>TC-{tc.testCaseId}</strong> —{" "}
                        {`${tc.product || "N/A"} | ${tc.module || "N/A"}`}
                        {i < cycle.length - 1 && " → "}
                      </div>
                    ))}
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
      </>
    );
  };

  return (
    <>
      <CustomDialog
        open={showDependencyModal}
        width={530}
        onClose={() => setShowDependencyModal(false)}
        title="Circular Dependencies Detected"
        content={renderDependencyContent()}
        actions={renderDependencyActions()}
      />
      <Box>
        {item.Step_Number !== 0 && (
          <Box mb={3}>
            <CustomSelect
              label={"Test Data Source"}
              options={TestDataOptions}
              width={"60%"}
              background={"#ffffff"}
              value={item.testdata_source}
              onChange={(e) => {
                handleChange(e.target.value, "testdata_source");
              }}
            />
          </Box>
        )}
        {item.testdata_source === "GenData" && (
          <Box display="flex" columnGap={2}>
            <CustomSelect
              label={"Generate Data Type"}
              options={GenDataOptions}
              width={"100%"}
              background={"#ffffff"}
              value={item.gendataType}
              onChange={(e) => {
                const selectedType = e.target.value;
                handleChangeMultiple({
                  gendataType: selectedType,
                  isRange: false,
                  rangeStart: "",
                  rangeEnd: "",
                  gendatalen: "",
                });
              }}
            />

            {item.gendataType === "TODAY" ? (
              <>
                <Box flex={1} display="flex" alignItems="center" gap={1}>
                  <Box flex={1}>
                    <CustomInput
                      placeholder="Date format (dd-MM-yyyy)"
                      labelinput="Date Format"
                      value={item.gendateformat}
                      onChange={(e) =>
                        handleGenChange(e.target.value, "generateDateFormat", item.TestData)
                      }
                      flex={1}
                    />
                  </Box>
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Supported Date Formats:
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2, listStyle: "none" }}>
                          <Typography variant="caption" component="li" sx={{ mb: 0.5 }}>
                            • dd-MM-yyyy → 17-02-2026
                          </Typography>
                          <Typography variant="caption" component="li" sx={{ mb: 0.5 }}>
                            • dd-MMM-yyyy → 17-FEB-2026
                          </Typography>
                          <Typography variant="caption" component="li" sx={{ mb: 0.5 }}>
                            • dd-MMMM-yyyy → 17-February-2026
                          </Typography>
                          <Typography variant="caption" component="li" sx={{ mb: 0.5 }}>
                            • MM/dd/yyyy → 02/17/2026
                          </Typography>
                          <Typography variant="caption" component="li">
                            • yyyy-MM-dd → 2026-02-17
                          </Typography>
                        </Box>
                      </Box>
                    }
                    arrow
                    placement="right"
                  >
                    <IconButton size="small" sx={{ mt: 3 }}>
                      <InfoOutlinedIcon fontSize="small" color="action" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <CustomInput
                  placeholder="Date offset (+/- days)"
                  labelinput="Date offset (+/- days)"
                  value={item.gendateoffset}
                  onChange={(e) =>
                    handleGenChange(e.target.value, "generateDateOffset", item.TestData)
                  }
                  flex={1}
                />
              </>
            ) : (
              item.gendataType !== "EMAIL" && (
                <Box display="flex" alignItems="flex-start" gap={2} flex={1}>
                  {item.gendataType === "NUMBER" && (
                    <FormControlLabel
                      sx={{ mt: 3, whiteSpace: "nowrap" }}
                      control={
                        <Switch
                          checked={!!item.isRange}
                          onChange={(e) => {
                            handleChangeMultiple({
                              isRange: e.target.checked,
                              gendatalen: "",
                              rangeStart: "",
                              rangeEnd: "",
                            });
                          }}
                        />
                      }
                      label="Use Range"
                    />
                  )}
                   {item.gendataType === "DECIMAL" && (
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 2 }}>
                      <CustomInput
                        onChange={(e) =>
                          handleChangeMultiple({
                            gendatalen: e.target.value.slice(0, 3),
                          })
                        }
                        placeholder="###"
                        labelinput="Digits Before"
                        value={item.gendatalen || ""}
                        flex={1}
                        type="number"
                        pattern="[0-9]*"
                      />

                      <Typography sx={{ mt: 3 }}>dot</Typography>

                      <CustomInput
                        onChange={(e) =>
                          handleChangeMultiple({
                            decimallen: e.target.value.slice(0, 3),
                          })
                        }
                        placeholder="###"
                        labelinput="Digits After"
                        value={item.decimallen || ""}
                        flex={1}
                        type="number"
                        pattern="[0-9]*"
                      />
                    </Box>
                  )}
                 
                  {item.gendataType === "NUMBER" && item.isRange ? (
                    <>
                      <CustomInput
                        onChange={(e) => handleChange(e.target.value, "rangeStart")}
                        placeholder="Start Range"
                        labelinput="Start Range"
                        value={item.rangeStart || rangeStart}
                        flex={1}
                        type="number"
                        pattern="[0-9]*"
                      />
                      <CustomInput
                        onChange={(e) => handleChange(e.target.value, "rangeEnd")}
                        placeholder="End Range"
                        labelinput="End Range"
                        value={item.rangeEnd || rangeEnd}
                        flex={1}
                        type="number"
                        pattern="[0-9]*"
                      />
                    </>
                  ) : (
                    item.gendataType !== "DECIMAL" && (
                      <CustomInput
                        onChange={(e) => handleChange(e.target.value, "gendatalen")}
                        placeholder="Generate Data Length"
                        labelinput="Generate Data Length"
                        value={item.gendatalen}
                        flex={1}
                        type="number"
                        pattern="[0-9]*"
                      />
                    )
                  )}
                </Box>
              )
            )}
          </Box>
        )}
        {item.testdata_source === "SendEmail" && (
          <Box display="flex" flexWrap="wrap" columnGap={2}>
            <CustomInput
              onChange={(e) => handleChange(e.target.value, "customerEmail")}
              placeholder="Enter email"
              labelinput="Email"
              value={item.customerEmail || ""}
              flex={1}
            />
            <CustomInput
              type="password"
              placeholder="Enter password"
              labelinput="Password"
              value={item.customerPassword || ""}
              onChange={(e) => handleChange(e.target.value, "customerPassword")}
              flex={1}
            />
            <CustomInput
              onChange={(e) => handleChange(e.target.value, "toaddress")}
              placeholder="Enter to address"
              labelinput="To address"
              value={item.toaddress || ""}
              flex={1}
            />
            <CustomInput
              onChange={(e) => handleChange(e.target.value, "emailsubject")}
              placeholder="Enter subject"
              labelinput="Subject"
              value={item.emailsubject || ""}
              flex={1}
            />
            <CustomInput
              onChange={(e) => handleChange(e.target.value, "emailcontent")}
              placeholder="Enter content"
              labelinput="Content"
              value={item.emailcontent || ""}
              flex={3}
            />
            {item.mailType === "Outlook" && (
              <>
                <CustomInput
                  onChange={(e) => handleChange(e.target.value, "tenantId")}
                  placeholder="Enter content"
                  labelinput="Tenant ID"
                  value={item.tenantId || ""}
                  flex={3}
                />
                <CustomInput
                  onChange={(e) => handleChange(e.target.value, "clientId")}
                  placeholder="Enter content"
                  labelinput="Client ID"
                  value={item.clientId || ""}
                  flex={3}
                />
              </>
            )}
          </Box>
        )}
        {item.testdata_source === "FromEmail" && (
          <Box display="flex" flexWrap="wrap" columnGap={2}>
            <CustomSelect
              label={"Mail Type"}
              options={[
                { label: "Basic", value: "Basic" },
                { label: "Outlook", value: "Outlook" },
              ]}
              width={"100%"}
              background={"#ffffff"}
              value={item.mailType}
              onChange={(e) => handleChange(e.target.value, "mailType")}
            />
            <CustomInput
              onChange={(e) => handleChange(e.target.value, "customerEmail")}
              placeholder="Enter email"
              labelinput="Email"
              value={item.customerEmail || ""}
              flex={1}
            />
            <CustomInput
              type="password"
              placeholder="Enter password"
              labelinput="Password"
              value={item.customerPassword || ""}
              onChange={(e) => handleChange(e.target.value, "customerPassword")}
              flex={1}
            />
            <CustomInput
              placeholder="Enter criterion"
              labelinput="Selection Criterion"
              value={item.EmailSelectionCriteria || ""}
              onChange={(e) => handleChange(e.target.value, "EmailSelectionCriteria")}
              flex={1}
            />
            <CustomInput
              placeholder="Enter filter"
              labelinput="Filter"
              value={item.EmailFilter || ""}
              onChange={(e) => handleChange(e.target.value, "EmailFilter")}
              flex={1}
            />
            {item.mailType === "Outlook" && (
              <>
                <CustomInput
                  onChange={(e) => handleChange(e.target.value, "tenantId")}
                  placeholder="Enter content"
                  labelinput="Tenant ID"
                  value={item.tenantId || ""}
                  flex={3}
                />
                <CustomInput
                  onChange={(e) => handleChange(e.target.value, "clientId")}
                  placeholder="Enter content"
                  labelinput="Client ID"
                  value={item.clientId || ""}
                  flex={3}
                />
              </>
            )}
          </Box>
        )}
        {item.testdata_source === "FromEmailDigits" && (
          <Box display="flex" flexWrap="wrap" columnGap={2}>
            <CustomSelect
              label={"Mail Type"}
              options={[
                { label: "Basic", value: "Basic" },
                { label: "Outlook", value: "Outlook" },
              ]}
              width={"100%"}
              background={"#ffffff"}
              value={item.mailType}
              onChange={(e) => handleChange(e.target.value, "mailType")}
            />
            <CustomInput
              onChange={(e) => handleChange(e.target.value, "customerEmail")}
              placeholder="Enter email"
              labelinput="Email"
              value={item.customerEmail || ""}
              flex={1}
            />
            <CustomInput
              type="password"
              placeholder="Enter password"
              labelinput="Password"
              value={item.customerPassword || ""}
              onChange={(e) => handleChange(e.target.value, "customerPassword")}
              flex={1}
            />
            <CustomInput
              placeholder="Enter criterion"
              labelinput="Selection Criterion"
              value={item.EmailSelectionCriteria || ""}
              onChange={(e) => handleChange(e.target.value, "EmailSelectionCriteria")}
              flex={1}
            />
            <CustomInput
              placeholder="Enter filter"
              labelinput="Filter"
              value={item.EmailFilter || ""}
              onChange={(e) => handleChange(e.target.value, "EmailFilter")}
              flex={1}
            />
            {item.mailType === "Outlook" && (
              <>
                <CustomInput
                  onChange={(e) => handleChange(e.target.value, "tenantId")}
                  placeholder="Enter content"
                  labelinput="Tenant ID"
                  value={item.tenantId || ""}
                  flex={3}
                />
                <CustomInput
                  onChange={(e) => handleChange(e.target.value, "clientId")}
                  placeholder="Enter content"
                  labelinput="Client ID"
                  value={item.clientId || ""}
                  flex={3}
                />
              </>
            )}
          </Box>
        )}

        {item.testdata_source === "FromSMS" && (
          <Box display="flex" columnGap={2}>
            <CustomInput
              onChange={(e) => handleChange(e.target.value, "regex")}
              placeholder="Enter Regex"
              labelinput="Regex"
              value={item.regex || ""}
            />
            <CustomSelect
              label={"SMS Method"}
              options={[
                { label: "Fetch OTP Direct", value: "fetchOtpDirect" },
                { label: "Fetch OTP Standard", value: "fetchOtpStandard" },
              ]}
              value={item.smsMethod || ""}
              onChange={(e) => handleChange(e.target.value, "smsMethod")}
            />
            {!isMobile && (
              <Box>
                <StyledLabel>Available Devices</StyledLabel>
                <CustomSelect
                  value={selectedDevice}
                  onChange={(e) => {
                    setSelectedDevice(e.target.value);
                    handleChange(e.target.value, "deviceId");
                  }}
                  options={
                    availableDevices?.length
                      ? availableDevices.map((device) => ({
                          label: device,
                          value: device,
                        }))
                      : [{ label: "No devices available", value: "" }]
                  }
                  placeholder="Select Device"
                  isLoading={loadingDevices}
                />
              </Box>
            )}
            <Box>
              <StyledLabel>System Id</StyledLabel>
              <CustomSelect
                value={item.systemId || ""}
                onChange={(e) => handleChange(e.target.value, "systemId")}
                options={
                  systemsid?.length
                    ? systemsid.map((id) => ({ label: id, value: id }))
                    : [{ label: "No system ids available", value: "" }]
                }
                placeholder="Select System Id"
              />
            </Box>
          </Box>
        )}
        {item.testdata_source === "FromSMSDigits" && (
          <Box display="flex" columnGap={2}>
            <CustomInput
              onChange={(e) => handleChange(e.target.value, "regex")}
              placeholder="Enter Regex"
              labelinput="Regex"
              value={item.regex || ""}
            />
            <CustomSelect
              label={"SMS Method"}
              options={[
                { label: "Fetch OTP Direct", value: "fetchOtpDirect" },
                { label: "Fetch OTP Standard", value: "fetchOtpStandard" },
              ]}
              value={item.smsMethod || ""}
              onChange={(e) => handleChange(e.target.value, "smsMethod")}
            />
            {!isMobile && (
              <Box>
                <StyledLabel>Available Devices</StyledLabel>
                <CustomSelect
                  value={selectedDevice}
                  onChange={(e) => {
                    setSelectedDevice(e.target.value);
                    handleChange(e.target.value, "deviceId");
                  }}
                  options={
                    availableDevices?.length
                      ? availableDevices.map((device) => ({
                          label: device,
                          value: device,
                        }))
                      : [{ label: "No devices available", value: "" }]
                  }
                  placeholder="Select Device"
                  isLoading={loadingDevices}
                />
              </Box>
            )}
            <Box>
              <StyledLabel>System Id</StyledLabel>
              <CustomSelect
                value={item.systemId || ""}
                onChange={(e) => handleChange(e.target.value, "systemId")}
                options={
                  systemsid?.length
                    ? systemsid.map((id) => ({ label: id, value: id }))
                    : [{ label: "No system ids available", value: "" }]
                }
                placeholder="Select System Id"
              />
            </Box>
          </Box>
        )}

        {item.testdata_source === "ChangeTestData" && (
          <CustomInput
            placeholder="Enter new data"
            labelinput="New Data"
            value={item.TestData || ""}
            onChange={(e) => handleChange(e.target.value, "TestData")}
            flex={1}
          />
        )}
        {item.testdata_source === "Date" && (
          <Box display="flex" columnGap={2}>
            <CustomInput
              placeholder="Enter date"
              labelinput="Date"
              value={item.TestData.split("~")[2] || ""}
              onChange={(e) => handleDateChange(e.target.value, "date", "TestData")}
              flex={1}
            />
            <CustomInput
              placeholder="Enter date format"
              labelinput="Date format"
              value={item.TestData.split("~")[3] || ""}
              onChange={(e) => handleDateFormat(e.target.value, "dateformat", "TestData")}
              flex={1}
            />
          </Box>
        )}
        {item.testdata_source === "FromApi" && (
          <Box display="flex" flexDirection="column" gap={2}>
            <ModuleSelector
              showTestCase={false}
              onModuleChange={(selection) => {
                if (selection) {
                  handleChangeMultiple({
                    apiproduct: selection.idproducts,
                    apimodule: selection.idmodules,
                  });
                  refetchAllApi();
                } else {
                  handleChangeMultiple({
                    apiproduct: "",
                    apimodule: "",
                    apiid: "",
                    apiparam: "",
                    DBQuery: "",
                    TestData: "",
                  });
                }
              }}
              initialValue={
                item.apiproduct && item.apimodule
                  ? {
                      idproducts: item.apiproduct,
                      idmodules: item.apimodule,
                    }
                  : null
              }
            />

            {item.apimodule !== "" && item.apimodule !== 0 && (
              <Box display="flex" gap={2}>
                <CustomSelect
                  label={"Api name"}
                  options={(Array.isArray(allApi) ? allApi : []).map((apiItem) => ({
                    label: apiItem.name,
                    value: apiItem.idapi,
                  }))}
                  width={"100%"}
                  background={"#ffffff"}
                  value={item.apiid}
                  onChange={(e, selected) => {
                    handleChangeMultiple({
                      apiid: e.target.value,
                      TestData: selected.props.children,
                    });
                  }}
                />

                <CustomInput
                  placeholder="Enter api Param"
                  labelinput="Api Param"
                  value={item.apiparam || ""}
                  onChange={(e) => handleChange(e.target.value, "apiparam")}
                  flex={1}
                />

                <CustomInput
                  placeholder="Enter api query"
                  labelinput="Api Query"
                  value={item.DBQuery || ""}
                  onChange={(e) => handleChange(e.target.value, "DBQuery")}
                  flex={1}
                />
              </Box>
            )}
          </Box>
        )}
        {item.testdata_source === "FromDB" && (
          <CustomInput
            placeholder="Enter DB query"
            labelinput="DB Query"
            value={item.DBQuery || ""}
            onChange={(e) => handleChange(e.target.value, "DBQuery")}
            flex={1}
          />
        )}
        {item.testdata_source === "Regex" && (
          <CustomInput
            placeholder="Enter Regex"
            labelinput="Regex"
            value={item.regex || ""}
            onChange={(e) => handleChange(e.target.value, "regex")}
            flex={1}
          />
        )}
        {item.testdata_source === "InDownloadedFile" && (
          <Box display="flex" columnGap={2}>
            <CustomSelect
              label={"Downloaded Files"}
              options={(downloadedFiles ?? [{ label: "", item: "" }]).map((fileItem) => ({
                label: fileItem,
                value: fileItem,
              }))}
              width={"100%"}
              background={"#ffffff"}
              value={item.filename.split("~")[0]}
              onChange={(e) => handleChange(e.target.value, "filename")}
            />
            <CustomInput
              placeholder="Enter assertion value"
              labelinput="Assertion value"
              value={item.filename.split("~")[1]}
              onChange={(e) => handleAssertionValueChange(e.target.value, "DBQuery")}
              flex={1}
            />
          </Box>
        )}
        {item.testdata_source === "UseVariableFromTheScenario" && (
          <CustomSelect
            label={"Use var from this scenario"}
            options={
              Array.isArray(recordedTestDataList?.list) && recordedTestDataList.list.length > 0
                ? recordedTestDataList.list.map((fileItem) => ({
                    label: fileItem.label || fileItem,
                    value: fileItem.value || fileItem,
                  }))
                : item.TestData
                  ? [{ label: item.TestData, value: item.TestData }]
                  : [{ label: "No data available", value: "" }]
            }
            width={"100%"}
            background={"#ffffff"}
            value={item.TestData}
            onChange={(e) => handleChange(e.target.value, "TestData")}
          />
        )}
        {item.testdata_source === "FromFile" && (
          <Box display="flex" columnGap={2} alignItems={"center"}>
            <Button
              sx={{ height: "50px", marginTop: "20px", minWidth: "150px" }}
              size="medium"
              variant="contained"
              component={"label"}
              startIcon={<CloudUploadIcon />}
            >
              Upload file
              <input type="file" hidden onChange={handleFileChange} />
            </Button>
            <CustomInput
              labelinput="File Name"
              value={item.filename}
              variant="outlined"
              disabled
              InputProps={{
                readOnly: true,
              }}
            />
            {(fileNameType === "xlsx" || fileNameType === "csv") && (
              <>
                <CustomInput
                  placeholder="Sheet"
                  labelinput="Sheet"
                  value={item.sheet}
                  onChange={(e) => handleChange(e.target.value, "sheet")}
                  sx={{ minWidth: "100px", maxWidth: "100px" }}
                />
                <CustomInput
                  placeholder="Cell"
                  labelinput="Cell"
                  value={item.cell}
                  onChange={(e) => handleChange(e.target.value, "cell")}
                  sx={{ minWidth: "100px", maxWidth: "100px" }}
                />
                <CustomSelect
                  label={"Scope"}
                  options={(scopeOptionList ?? [{ label: "", item: "" }]).map((item) => ({
                    label: item.value,
                    value: item.label,
                  }))}
                  width={"100%"}
                  background={"#ffffff"}
                  value={item.scope}
                  onChange={(e) => handleChange(e.target.value, "scope")}
                />
                {item.scope === "Multi Execution" && (
                  <>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={item?.continue_prev_exec === true || item?.continue_prev_exec === "true"}
                          onChange={(e) => handleChange(e.target.checked, "continue_prev_exec")}
                        />
                      }
                      label="Resume Previous Run"
                    />
                    <Box display="flex" alignItems="center" gap={1}>
                      <Button
                        variant="contained"
                        sx={{ height: "50px", marginTop: "20px", minWidth: "150px" }}
                        onClick={handleReset}
                        disabled={isResetting}
                      >
                        Reset Iteration
                      </Button>
                      <Tooltip
                        title="In multi execution we pick the last cell and go to the next cell to pick the value. This button will reset the last cell and make it pick from start."
                        arrow
                      >
                        <IconButton sx={{ marginTop: "20px" }}>
                          <InfoOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </>
                )}
                {item?.scope === scopeOptionList[0].value && (
                  <CustomInput
                    placeholder="End Range"
                    labelinput="End Range"
                    value={item.endRange}
                    onChange={(e) => handleChange(e.target.value, "endRange")}
                  />
                )}
              </>
            )}
            {fileNameType === "json" && (
              <>
                <CustomInput
                  placeholder="Query"
                  labelinput="Query"
                  value={item.filter}
                  onChange={(e) => handleChange(e.target.value, "filter")}
                />
                <CustomSelect
                  label={"Scope"}
                  options={(scopeOptionList ?? [{ label: "", item: "" }]).map((item) => ({
                    label: item.value,
                    value: item.label,
                  }))}
                  width={"100%"}
                  background={"#ffffff"}
                  value={item.scope}
                  onChange={(e) => handleChange(e.target.value, "scope")}
                />
                {item?.scope === scopeOptionList[0].value && (
                  <CustomInput
                    placeholder="End Range"
                    labelinput="End Range"
                    value={item.endRange}
                    onChange={(e) => handleChange(e.target.value, "endRange")}
                  />
                )}
              </>
            )}
          </Box>
        )}
        {item.testdata_source === "FromReferenceFile" && (
          <Box display="flex" columnGap={2}>
            <CustomSelect
              label={"Select file"}
              options={
                Array.isArray(refFiles?.list) && refFiles?.list.length > 0
                  ? refFiles.list.map((fileItem) => ({
                      label: fileItem.label || fileItem,
                      value: fileItem.value || fileItem,
                    }))
                  : [{ label: "No files available", value: "" }]
              }
              width={"100%"}
              background={"#ffffff"}
              value={item.filename}
              onChange={handleRefFileChange}
            />
            {(fileNameType === "xlsx" || fileNameType === "csv") && (
              <>
                <CustomInput
                  placeholder="Sheet"
                  labelinput="Sheet"
                  value={item.sheet}
                  onChange={(e) => handleChange(e.target.value, "sheet")}
                />
                <CustomInput
                  placeholder="Cell"
                  labelinput="Cell"
                  value={item.cell}
                  onChange={(e) => handleChange(e.target.value, "cell")}
                />
                <CustomSelect
                  label={"Scope"}
                  options={(scopeOptionList ?? [{ label: "", item: "" }]).map((item) => ({
                    label: item.value,
                    value: item.label,
                  }))}
                  width={"100%"}
                  background={"#ffffff"}
                  value={item.scope}
                  onChange={(e) => handleChange(e.target.value, "scope")}
                />
                {item.scope === "Multi Execution" && (
                  <>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={item?.continue_prev_exec === true || item?.continue_prev_exec === "true"}
                          onChange={(e) => handleChange(e.target.checked, "continue_prev_exec")}
                        />
                      }
                      label="Resume Previous Run"
                    />
                    <Box display="flex" alignItems="center" gap={1}>
                      <Button
                        variant="contained"
                        sx={{ height: "50px", marginTop: "20px", minWidth: "150px" }}
                        onClick={handleReset}
                        disabled={isResetting}
                      >
                        Reset Iteration
                      </Button>
                      <Tooltip
                        title="In multi execution we pick the last cell and go to the next cell to pick the value. This button will reset the last cell and make it pick from start."
                        arrow
                      >
                        <IconButton sx={{ marginTop: "20px" }}>
                          <InfoOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </>
                )}
                {item?.scope === scopeOptionList[0].value && (
                  <CustomInput
                    placeholder="End Range"
                    labelinput="End Range"
                    value={item.endRange}
                    onChange={(e) => handleChange(e.target.value, "endRange")}
                  />
                )}
              </>
            )}
            {fileNameType === "json" && (
              <>
                <CustomInput
                  placeholder="Query"
                  labelinput="Query"
                  value={item.filter}
                  onChange={(e) => handleChange(e.target.value, "filter")}
                />
                <CustomSelect
                  label={"Scope"}
                  options={(scopeOptionList ?? [{ label: "", item: "" }]).map((item) => ({
                    label: item.value,
                    value: item.label,
                  }))}
                  width={"100%"}
                  background={"#ffffff"}
                  value={item.scope}
                  onChange={(e) => handleChange(e.target.value, "scope")}
                />
                {item?.scope === scopeOptionList[0].value && (
                  <CustomInput
                    placeholder="End Range"
                    labelinput="End Range"
                    value={item.endRange}
                    onChange={(e) => handleChange(e.target.value, "endRange")}
                  />
                )}
              </>
            )}
          </Box>
        )}
        {item.testdata_source === "GoogleSheet" && (
          <Box display="flex" columnGap={2}>
            <CustomInput
              placeholder="Sheet Id"
              labelinput="Sheet Id"
              value={item.filename}
              onChange={(e) => handleChange(e.target.value, "filename")}
            />
            <CustomInput
              placeholder="Sheet"
              labelinput="Sheet"
              value={item.sheet}
              onChange={(e) => handleChange(e.target.value, "sheet")}
            />
            <CustomInput
              placeholder="Cell"
              labelinput="Cell"
              value={item.cell}
              onChange={(e) => handleChange(e.target.value, "cell")}
            />
            <CustomSelect
              label={"Scope"}
              options={(scopeOptionList ?? [{ label: "", item: "" }]).map((item) => ({
                label: item.value,
                value: item.label,
              }))}
              width={"100%"}
              background={"#ffffff"}
              value={item.scope}
              onChange={(e) => handleChange(e.target.value, "scope")}
            />
            {item?.scope === scopeOptionList[0].value && (
              <CustomInput
                placeholder="End Range"
                labelinput="End Range"
                value={item.endRange}
                onChange={(e) => handleChange(e.target.value, "endRange")}
              />
            )}
          </Box>
        )}
        {item.testdata_source === "LastRun" && (
                <Box display="flex" columnGap={2}>
                  <CustomSelect
                    label={"Step Number"}
                    options={(allTestSteps ?? []).map((step) => ({
                      label: `Step ${step.Step_Number}`,
                      value: step.idtest_step,
                    }))}
                    width={"300px"}
                    background={"#ffffff"}
                    value={item.lastRunStep || ""}
                    onChange={(e) => handleChange(e.target.value, "lastRunStep")}
                  />
                  <CustomSelect
                    label={"Select Field Name"}
                    options={[{ label: "Test Data", value: "TestData" }]}
                    width={"300px"}
                    background={"#ffffff"}
                    value={item.lastRunTestData || ""}
                    onChange={(e) => handleChange(e.target.value, "lastRunTestData")}
                  />
                </Box>
              )}
        {item.testdata_source === "DependentonOtherTestScenario" && (
          <Box display="flex" flexDirection="column" gap={2}>
            <TestCaseSearch
              onTestCaseSelect={(selectedTestCase) => {
                if (selectedTestCase) {
                  handleChangeMultiple({
                    DependentTC: selectedTestCase.Test_Case,
                    dependentTC: selectedTestCase.idtest_case,
                    DependentProduct: selectedTestCase.idproducts,
                    DependentModule: selectedTestCase.idmodules,
                    apiproduct: selectedTestCase.idproducts,
                    apimodule: selectedTestCase.idmodules,
                  });
                  setSelectedTestCase(selectedTestCase.idtest_case);
                  refetchRecordedTestDataList();
                } else {
                  handleChangeMultiple({
                    DependentProduct: "",
                    DependentModule: "",
                    DependentTC: "",
                    dependentTC: 0,
                  });
                }
              }}
              initialValue={
                item.DependentTC && item.DependentProduct && item.DependentModule
                  ? {
                      idtest_case: item.dependentTC,
                      idproducts: item.DependentProduct,
                      idmodules: item.DependentModule,
                      Test_Case: item.DependentTC,
                    }
                  : null
              }
              tabId="dependent"
            />

            {item.DependentTC !== "" && item.DependentTC !== 0 && (
              <CustomSelect
                label={"Use var from this scenario"}
                options={
                  Array.isArray(recordedTestDataList?.list) && recordedTestDataList.list.length > 0
                    ? recordedTestDataList.list.map((fileItem) => ({
                        label: fileItem.label || fileItem,
                        value: fileItem.value || fileItem,
                      }))
                    : [{ label: "No data available", value: "" }]
                }
                width={"100%"}
                background={"#ffffff"}
                value={item.TestData || ""}
                onChange={(e) =>
                  handleChangeMultiple({ useVar: e.target.value, TestData: e.target.value })
                }
              />
            )}
          </Box>
        )}
        <Box mt={2} sx={{ display: "flex", flexDirection: "row", rowGap: 2 }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              item.testdata_source === "DependentonOtherTestScenario" && !Number(selectedTestCase) ||
              (item.testdata_source === "FromFile" && !hasFile)
            }
          >
            Submit
          </Button>
          <div className="ml-8">
            <HelpIconButton path="/test-data-source" />
          </div>
        </Box>
      </Box>
    </>
  );
};
export default TestDataSetup;
